// SEO growth landing pages: 8 Ontario city pages + 8 industry x service combo
// pages. Rendered by the existing AuthorityPage template. Same schema shape as
// AUTHORITY_PAGES in /data/authority.js. Copy is in Kostaja voice: outcome-first,
// plain English, no em dashes, no fake metrics.

const cityCommonFaq = (city) => ([
  {
    q: `Does Kostaja work with businesses in ${city} remotely, or in person?`,
    a: `Both. Kostaja works with ${city} service businesses remotely by default, with in-region visits arranged when the project benefits from it. Most of the work, including strategy, brand, website, and sales materials, does not require an in-person meeting.`,
  },
  {
    q: `How much does branding or a website cost for a ${city} service business?`,
    a: `Fixed-scope work for a ${city} service business typically falls between one thousand and ten thousand Canadian dollars. Larger connected projects, such as brand and website together, are quoted after a short review. Every price is written down before work starts.`,
  },
  {
    q: `What is the fastest first move for a ${city} service business that already has a site?`,
    a: `A short audit. Kostaja looks at brand, website, Google Business Profile, and reviews together, and returns a short list of the fixes that will move the needle first. Most owners do not need everything rebuilt.`,
  },
]);

const citySection = (city, positioning, industries) => [
  {
    h: `Brand and marketing for ${city} service businesses`,
    body: [
      `Kostaja builds the public-facing part of a ${city} service business: brand, website, Google Business Profile, reviews, social presence, sales materials, and AI-supported follow-up. All of it is designed so ${city} customers can decide, in the first ten seconds, that the business is worth a call.`,
      positioning,
    ],
  },
  {
    h: `Who this is for in ${city}`,
    rows: industries.map((r) => ({ k: r.k, v: r.v })),
  },
  {
    h: `What ${city} operators actually need`,
    body: [
      `Most ${city} service businesses are not losing customers to competitors with better ads. They are losing them to competitors with sharper first impressions. A brand that reads current, a website that answers the real question, a Google profile that is finished, and a review flow that keeps working while the owner works.`,
      `Kostaja builds all of that as one connected system, not four separate invoices.`,
    ],
  },
  {
    h: `How Kostaja works with ${city} businesses`,
    body: [
      `Every engagement starts with a written scope. Every price is agreed before work begins. Every deliverable is named in plain English. Studio leadership owns the strategy, the writing, and the design through delivery. There is no hand-off after the sales call.`,
    ],
    links: [
      { label: "Explore services", to: "/services" },
      { label: "Apply to work with Kostaja", to: "/waitlist" },
      { label: "See the marketing stack", to: "/service-business-marketing-canada" },
    ],
  },
];

export const GROWTH_PAGES = {
  // ---------------- ONTARIO CITY PAGES (8) ----------------
  "toronto-brand-marketing-agency": {
    key: "torontoBrandAgency",
    slug: "toronto-brand-marketing-agency",
    eyebrow: "Local · Toronto",
    h1: "Brand Marketing Agency in Toronto",
    lead:
      "Kostaja is a Canadian brand marketing agency working with Toronto service businesses across the GTA. Brand, website, local visibility, and sales materials built for owners who need a sharper public presence without a big-agency budget.",
    sections: citySection(
      "Toronto",
      "Toronto is one of the most competitive service markets in North America. A finished brand, a working website, and a real Google Business Profile are no longer optional. They are the price of admission.",
      [
        { k: "Trades and home services", v: "Contractors, HVAC, electricians, roofers, and renovation firms across Toronto and the GTA." },
        { k: "Real estate and mortgage", v: "Toronto agents, teams, brokerages, and mortgage professionals who need a personal brand that reads senior." },
        { k: "Health and wellness", v: "Downtown clinics, dental practices, physiotherapy, and esthetics operators." },
        { k: "Professional services", v: "Toronto lawyers, accountants, consultants, and boutique firms." },
        { k: "Restaurants and hospitality", v: "Neighbourhood restaurants, cafes, and hospitality operators from Queen West to Scarborough." },
        { k: "Retail and independent brands", v: "Small Toronto retailers and product brands that need cohesion across packaging, storefront, and social." },
      ]
    ),
    faqs: [
      {
        q: "Is Kostaja based in Toronto?",
        a: "Kostaja is a Canadian brand marketing agency serving Toronto and Ontario. The studio operates remotely by default, with in-region visits arranged when a project benefits from it.",
      },
      ...cityCommonFaq("Toronto"),
      {
        q: "Does Kostaja help Toronto businesses with Google Maps rankings?",
        a: "Yes. Kostaja completes and optimizes Google Business Profiles, standardizes local citations, and builds a review flow. Kostaja does not guarantee specific map rankings. It builds the foundation that gives a Toronto business a real shot at being found for the queries that matter.",
      },
    ],
  },

  "ottawa-brand-marketing-agency": {
    key: "ottawaBrandAgency",
    slug: "ottawa-brand-marketing-agency",
    eyebrow: "Local · Ottawa",
    h1: "Make your Ottawa business easier to find, trust, and choose.",
    lead:
      "Kostaja builds websites, brands, local search visibility, content, and sales materials for Ottawa service businesses that have outgrown the way they look online.",
    sections: [
      {
        h: "What Kostaja does for Ottawa service businesses",
        body: [
          "Kostaja builds and improves the public-facing system around the business. Website design, brand, Google Business Profile and local search, content, print, and sales materials. Not four invoices from four vendors. One studio that owns the result from strategy through delivery.",
          "The work is usually remote by default, with in-region visits arranged when a project benefits from it. Most engagements, including strategy, brand, website, and sales materials, do not require an in-person meeting.",
        ],
      },
      {
        h: "Who this is for in Ottawa",
        rows: [
          { k: "Government-adjacent services", v: "Consultants, IT firms, and professional operators that sell into government-adjacent buyers across the National Capital Region." },
          { k: "Health and wellness", v: "Clinics, medical practices, dental offices, and wellness operators across Ottawa and the surrounding neighbourhoods." },
          { k: "Trades and home services", v: "Renovation, HVAC, roofing, and home service operators serving Ottawa, Kanata, Orléans, Barrhaven, and across the river into Gatineau." },
          { k: "Real estate and mortgage", v: "Ottawa agents, teams, and mortgage professionals who need a personal brand that reads senior." },
          { k: "Restaurants and hospitality", v: "Independent restaurants, cafes, and hospitality operators across the ByWard Market, Wellington West, Westboro, and beyond." },
          { k: "Technology and startups", v: "Kanata and downtown Ottawa technology operators that need to look ready before the next raise or hire." },
        ],
      },
      {
        h: "What usually needs to improve first",
        body: [
          "Most Ottawa service businesses do not need a bigger ad budget. They need the parts customers already see to do a better job. A website that explains the services. A Google Business Profile that is finished. A brand that reads consistent. Sales materials that advance the conversation after the first call.",
          "Kostaja fixes those pieces as one connected system so new attention actually converts.",
        ],
      },
      {
        h: "How we work with Ottawa businesses",
        body: [
          "Every engagement starts with a written scope. Every price is agreed before work begins. Every deliverable is named in plain English. Studio leadership owns the strategy, writing, and design through delivery. There is no hand-off after the sales call.",
        ],
        links: [
          { label: "View Ottawa web design", to: "/ottawa-web-design" },
          { label: "View Ottawa branding", to: "/ottawa-branding-agency" },
          { label: "View Ottawa local SEO", to: "/ottawa-local-seo" },
          { label: "View all services", to: "/services" },
        ],
      },
    ],
    faqs: [
      {
        q: "Does Kostaja work with Ottawa businesses in French?",
        a: "Copy is written in English by default. Bilingual site builds and bilingual sales materials are handled through vetted French copy partners. Ask for a bilingual scope during your strategy call.",
      },
      {
        q: "Is Kostaja based in Ottawa?",
        a: "Kostaja is a Canadian studio serving Ottawa and the Ottawa Valley. The studio operates remotely by default, with in-region visits arranged when the project benefits from it.",
      },
      ...cityCommonFaq("Ottawa"),
    ],
  },

  "ottawa-web-design": {
    key: "ottawaWebDesign",
    slug: "ottawa-web-design",
    eyebrow: "Ottawa · Web Design",
    h1: "Build a website people understand and act on.",
    lead:
      "Website design for Ottawa service businesses that need stronger service pages, better proof, easier mobile contact, faster performance, and a clearer path from search to inquiry.",
    sections: [
      {
        h: "Who this is for",
        body: [
          "Ottawa service businesses with a site that no longer reflects the business behind it. Operations that have grown, added services, hired people, won work, and raised their standard, while the site still describes who they were three years ago.",
          "Also appropriate for Ottawa operators preparing to launch a new service, bring on a new partner, or hand off referrals to a public link they do not currently feel good about sending.",
        ],
      },
      {
        h: "What usually needs to improve first",
        rows: [
          { k: "Service pages nobody can find", v: "Services buried under a single page, no individual URLs, no search structure." },
          { k: "A homepage trying to say everything", v: "Multiple messages competing before anyone has decided what the business is." },
          { k: "No proof near the decision", v: "The decision happens on the service page. The proof lives on a separate case-study page nobody clicks to." },
          { k: "Contact paths buried too deep", v: "Phone and inquiry hidden behind two menus on mobile, where most Ottawa buyers are deciding." },
          { k: "A website that looks behind the business", v: "The site reads as an older, smaller, less-capable version of what the operation actually is now." },
          { k: "Weak location signals", v: "No clear Ottawa service area, no local address signals, no map, no local pages for Google to understand." },
        ],
      },
      {
        h: "What Kostaja builds",
        body: [
          "A website structured around the real buying decision. Clear service pages for each offer. Proof placed where the decision actually happens. A homepage that explains the business in the first ten seconds. Mobile call, text, and inquiry paths that need no instructions. Fast loads, accessible markup, and search structure that Google can read without guessing.",
          "Every build is reviewed against speed, mobile usability, accessibility, local search signals, and the one next step the page is asking the visitor to take.",
        ],
      },
      {
        h: "When not to buy a full rebuild",
        body: [
          "If the structure and content still work and only the look is dated, a focused redesign on the existing platform is often the better use of the budget. Kostaja will say so after a short review. We do not sell a rebuild to a business that does not need one.",
        ],
        links: [
          { label: "Website audit", to: "/services/website-audit" },
          { label: "Website design service", to: "/services/website-design" },
          { label: "Website services hub", to: "/services/websites" },
          { label: "Ottawa branding", to: "/ottawa-branding-agency" },
          { label: "Ottawa local SEO", to: "/ottawa-local-seo" },
        ],
      },
    ],
    faqs: [
      {
        q: "How much should an Ottawa service business expect to pay for a website?",
        a: "Fixed-scope websites for an Ottawa service business typically fall between one thousand five hundred and ten thousand Canadian dollars, depending on how many services the site covers and whether a brand refresh is in scope. Larger connected brand-and-website projects are quoted after a short review. Every price is written down before work starts.",
      },
      {
        q: "Does Kostaja handle hosting and ongoing care?",
        a: "Yes. Ongoing website care is available as an optional retainer after launch and covers hosting handling, performance monitoring, content updates, and small design changes. It is not required.",
      },
      {
        q: "How long does an Ottawa website build take?",
        a: "A focused service-business website typically takes four to eight weeks from scope to launch, depending on content readiness. Brand-and-website projects run longer because the brand decisions have to be settled before the pages can be built around them.",
      },
      ...cityCommonFaq("Ottawa"),
    ],
  },

  "ottawa-branding-agency": {
    key: "ottawaBrandingAgency",
    slug: "ottawa-branding-agency",
    eyebrow: "Ottawa · Branding",
    h1: "Make the business look as serious as it is.",
    lead:
      "Brand strategy and identity systems for Ottawa businesses that have outgrown an inconsistent, dated, or incomplete public presence.",
    sections: [
      {
        h: "What a brand actually fixes",
        body: [
          "A brand is not a logo. It is how the business is recognized, understood, and trusted across every place a customer encounters it. The website. Google. Social profiles. Signs. Vehicles. Print. Sales materials. Email signatures. When those pieces disagree, the business feels smaller and less sure of itself than it actually is.",
          "Kostaja builds brand systems for Ottawa operators who need the public-facing version of the business to catch up with the operation behind it.",
        ],
      },
      {
        h: "Who this is for in Ottawa",
        rows: [
          { k: "Established operators with inconsistent identity", v: "The business has grown. The visual system has not kept up. Each new material looks slightly different from the last." },
          { k: "Operators preparing to raise, hire, or sell", v: "A credible brand reduces friction in every one of those conversations." },
          { k: "Partnerships, mergers, and acquired books", v: "A combined brand needs to settle the identity question before marketing works at all." },
          { k: "Second-generation service businesses", v: "The business the parents built still works. The brand needs to reflect what it is now." },
        ],
      },
      {
        h: "What Kostaja delivers",
        body: [
          "A written brand foundation that names what the business is, who it is for, and what makes it worth choosing. An identity system including wordmark, mark, colour, type, and application rules. Templates for the materials the business actually produces, so the system stays consistent after Kostaja leaves.",
          "Scope depends on the business. Some operators need a focused refresh. Some need a full rebuild. Kostaja will say which, after a short review.",
        ],
      },
      {
        h: "Related services",
        links: [
          { label: "Brand audit", to: "/services/brand-audit" },
          { label: "Brand strategy", to: "/services/brand-strategy" },
          { label: "Branding hub", to: "/services/brand" },
          { label: "Ottawa web design", to: "/ottawa-web-design" },
          { label: "Ottawa local SEO", to: "/ottawa-local-seo" },
        ],
      },
    ],
    faqs: [
      {
        q: "Do I need a full rebrand or just an identity refresh?",
        a: "A refresh is usually enough when the business name, positioning, and audience still work and only the visual system has fallen behind. A rebuild is appropriate when the business has materially changed, when the name or positioning no longer fits, or when the existing identity is actively hurting the sales conversation. Kostaja will recommend one or the other after a short brand audit.",
      },
      {
        q: "Can Kostaja build a brand and website together?",
        a: "Yes. Connected brand-and-website projects are the most common Ottawa engagement because the brand decisions and the website structure answer the same questions. Scoping them together is usually faster and less expensive than running them as two separate projects.",
      },
      ...cityCommonFaq("Ottawa"),
    ],
  },

  "ottawa-local-seo": {
    key: "ottawaLocalSeo",
    slug: "ottawa-local-seo",
    eyebrow: "Ottawa · Local SEO",
    h1: "Show up when Ottawa customers are ready to choose.",
    lead:
      "Kostaja improves Google Business Profile, website location signals, service information, reviews, local content, and search structure so Ottawa service businesses are easier to find and trust.",
    sections: [
      {
        h: "What local search is really about",
        body: [
          "Local search is not a ranking exercise. It is the moment an Ottawa customer searches for the service, sees the business, and decides whether to call. The map result, the reviews, the photos, the service list, the hours, and the website link all do work in that moment.",
          "Kostaja finishes the parts that are usually incomplete so the business is easier to evaluate the second it appears.",
        ],
      },
      {
        h: "What usually needs to improve first",
        rows: [
          { k: "An incomplete Google Business Profile", v: "Missing services, missing photos, outdated hours, no service area, generic description, no categories beyond the primary one." },
          { k: "Thin or missing service information", v: "The profile and the website do not actually name the services the business provides." },
          { k: "A broken review flow", v: "No clear path for happy customers to leave a review. Negative reviews answered defensively or ignored." },
          { k: "Weak location signals on the website", v: "No service area, no city pages for the legitimate markets served, no local structured data." },
          { k: "No reason for Google to update anything", v: "The profile has not posted an update, a photo, or a service change in months." },
        ],
      },
      {
        h: "What Kostaja builds",
        body: [
          "A finished Google Business Profile with correct categories, complete services, real photos, and a steady posting rhythm. A website that supports the profile with service pages, service-area pages for the legitimate markets, correct structured data, and a visible review flow.",
          "Ongoing growth support is available as a retainer. It is not required.",
        ],
      },
      {
        h: "Related services",
        links: [
          { label: "Local SEO snapshot", to: "/services/local-seo-snapshot" },
          { label: "Google Business Profile setup", to: "/services/google-business-profile-setup" },
          { label: "GBP optimization", to: "/services/google-business-profile-optimization" },
          { label: "GBP growth support", to: "/services/google-business-profile-growth-support" },
          { label: "Local search hub", to: "/services/google-business-profile-local-seo" },
          { label: "Ottawa web design", to: "/ottawa-web-design" },
        ],
      },
    ],
    faqs: [
      {
        q: "Can Kostaja guarantee a top-three Google Maps ranking?",
        a: "No. Nobody can honestly guarantee a specific map ranking. Kostaja finishes the Google Business Profile, improves the website signals Google uses to understand the business, and builds a review flow. That gives the business a real shot at being found for the queries that matter. Guarantees come from vendors trying to close the sale, not from search.",
      },
      {
        q: "Do I need a separate page for every Ottawa neighbourhood?",
        a: "No. A single Ottawa service page handles the main search demand. A separate page is only appropriate when a surrounding community has distinct search demand and the business genuinely serves that community in a differentiated way. Mass-produced neighbourhood pages hurt more than they help.",
      },
      ...cityCommonFaq("Ottawa"),
    ],
  },

  "mississauga-brand-marketing-agency": {
    key: "mississaugaBrandAgency",
    slug: "mississauga-brand-marketing-agency",
    eyebrow: "Local · Mississauga",
    h1: "Brand Marketing Agency in Mississauga",
    lead:
      "Kostaja helps Mississauga service businesses stop losing work to sharper-looking competitors from Toronto and Oakville. Brand, website, local visibility, and sales materials built for owners who want to look bigger than one location.",
    sections: citySection(
      "Mississauga",
      "Mississauga service businesses compete against Toronto quality and Oakville polish. A finished brand and a website that answers the real question level the playing field faster than paid ads ever will.",
      [
        { k: "Home services and trades", v: "Mississauga contractors, cleaning services, HVAC, and roofers serving Peel Region." },
        { k: "Automotive", v: "Independent auto shops, detailers, and dealership-adjacent operators." },
        { k: "Professional services", v: "Accountants, lawyers, immigration consultants, and boutique firms serving the GTA West." },
        { k: "Health and wellness", v: "Family clinics, dental practices, and physiotherapy operators across Mississauga and Streetsville." },
        { k: "Retail and food", v: "Independent retailers, ethnic grocers, and neighbourhood restaurants across Mississauga." },
      ]
    ),
    faqs: cityCommonFaq("Mississauga"),
  },

  "hamilton-brand-marketing-agency": {
    key: "hamiltonBrandAgency",
    slug: "hamilton-brand-marketing-agency",
    eyebrow: "Local · Hamilton",
    h1: "Brand Marketing Agency in Hamilton",
    lead:
      "Kostaja helps Hamilton service businesses look as capable online as they are in person. Brand, website, local search, and sales materials for trades, health, hospitality, and independent operators across the Hamilton area.",
    sections: citySection(
      "Hamilton",
      "Hamilton service businesses have earned reputations offline that their online presence does not reflect. Fixing that gap is the fastest way to attract higher-value customers without changing the actual work.",
      [
        { k: "Trades and home services", v: "Hamilton contractors, renovation firms, roofers, and mechanical operators serving Hamilton and Burlington." },
        { k: "Health and wellness", v: "Independent clinics and practices across Hamilton and Ancaster." },
        { k: "Hospitality and food", v: "James Street North, Locke Street, and Hamilton Mountain restaurants, cafes, and bars." },
        { k: "Manufacturing-adjacent services", v: "B2B service firms serving Hamilton's industrial base." },
        { k: "Real estate and mortgage", v: "Hamilton agents and mortgage professionals competing across the Golden Horseshoe." },
      ]
    ),
    faqs: cityCommonFaq("Hamilton"),
  },

  "kitchener-waterloo-brand-marketing-agency": {
    key: "kitchenerWaterlooBrandAgency",
    slug: "kitchener-waterloo-brand-marketing-agency",
    eyebrow: "Local · Kitchener-Waterloo",
    h1: "Brand Marketing Agency in Kitchener-Waterloo",
    lead:
      "Kostaja helps Kitchener-Waterloo service businesses and early-stage teams present as the serious operations they are. Brand, website, and marketing systems that hold up in front of both local buyers and national customers.",
    sections: citySection(
      "Kitchener-Waterloo",
      "Kitchener-Waterloo buyers are educated, comparison-driven, and often technical. A brand and website that read confident and clear do more work here than any single ad campaign.",
      [
        { k: "Startups and tech", v: "Early-stage KW teams that need a landing page, brand, and deck that all match." },
        { k: "Professional services", v: "Waterloo accountants, lawyers, consultants, and B2B service firms." },
        { k: "Trades and home services", v: "Renovation, HVAC, and home service operators serving Waterloo Region." },
        { k: "Health and wellness", v: "Clinics, dental, physiotherapy, and wellness practices across Kitchener, Waterloo, and Cambridge." },
        { k: "Local retail and food", v: "Uptown Waterloo and downtown Kitchener independent operators." },
      ]
    ),
    faqs: cityCommonFaq("Kitchener-Waterloo"),
  },

  "london-ontario-brand-marketing-agency": {
    key: "londonOntarioBrandAgency",
    slug: "london-ontario-brand-marketing-agency",
    eyebrow: "Local · London Ontario",
    h1: "Brand Marketing Agency in London, Ontario",
    lead:
      "Kostaja helps London service businesses build a public presence that finally matches the work. Brand, website, local visibility, and sales materials for owners who want to be the obvious local choice.",
    sections: citySection(
      "London",
      "London service businesses often compete on trust more than price. Fixing the brand, the website, and the Google profile together is the fastest way to earn that trust before the first call.",
      [
        { k: "Trades and home services", v: "London contractors, renovation firms, roofers, and HVAC operators." },
        { k: "Health and wellness", v: "Independent clinics, dental, and wellness practices across the London area." },
        { k: "Education-adjacent services", v: "Tutoring, coaching, consulting, and B2B services that sell into Western University and Fanshawe communities." },
        { k: "Hospitality and food", v: "Richmond Row, Old East Village, and downtown London restaurants and cafes." },
        { k: "Automotive and mechanical", v: "Independent shops, detailers, and small dealerships across London and surrounding communities." },
      ]
    ),
    faqs: cityCommonFaq("London"),
  },

  "oakville-brand-marketing-agency": {
    key: "oakvilleBrandAgency",
    slug: "oakville-brand-marketing-agency",
    eyebrow: "Local · Oakville",
    h1: "Brand Marketing Agency in Oakville",
    lead:
      "Kostaja helps Oakville service businesses show up as the premium option they are. Brand, website, and sales materials designed for owners whose customers expect polish before they pick up the phone.",
    sections: citySection(
      "Oakville",
      "Oakville buyers pay more, and they expect more before they call. A brand that reads senior, a website that respects the reader, and materials that hold up in a professional meeting close deals here that would stall anywhere else.",
      [
        { k: "Home services and premium trades", v: "High-end renovation, custom builders, landscape design, and premium home service firms." },
        { k: "Professional services", v: "Oakville accountants, lawyers, financial advisors, and consulting firms." },
        { k: "Health and wellness", v: "Private clinics, esthetics, cosmetic dentistry, and premium wellness practices." },
        { k: "Real estate", v: "Oakville agents and teams selling in a price band where the brand quietly does half the work." },
        { k: "Retail and lifestyle", v: "Kerr Village, Bronte Village, and downtown Oakville independent brands." },
      ]
    ),
    faqs: cityCommonFaq("Oakville"),
  },

  "burlington-brand-marketing-agency": {
    key: "burlingtonBrandAgency",
    slug: "burlington-brand-marketing-agency",
    eyebrow: "Local · Burlington",
    h1: "Brand Marketing Agency in Burlington",
    lead:
      "Kostaja helps Burlington service businesses build the sharper, calmer public presence their customers already expect. Brand, website, local search, and sales materials that make the business easier to trust and easier to refer.",
    sections: citySection(
      "Burlington",
      "Burlington is a referral market. A finished brand, a website that answers the real question, and a Google profile that stays alive convert referrals into booked work at a rate paid ads cannot match.",
      [
        { k: "Home services", v: "Burlington contractors, HVAC, cleaning, and residential service operators." },
        { k: "Professional services", v: "Accountants, lawyers, and consultants serving Burlington and West Halton." },
        { k: "Health and wellness", v: "Clinics, dental, and wellness practices across Burlington and Aldershot." },
        { k: "Real estate and mortgage", v: "Burlington agents and mortgage professionals working across the Golden Horseshoe." },
        { k: "Retail and food", v: "Downtown Burlington and Roseland-area independent operators." },
      ]
    ),
    faqs: cityCommonFaq("Burlington"),
  },

  // ---------------- OTTAWA VALLEY HUB + 7 LOCATION PAGES ----------------
  "ottawa-valley-marketing-agency": {
    key: "ottawaValleyMarketingAgency",
    slug: "ottawa-valley-marketing-agency",
    eyebrow: "Regional · Ottawa Valley",
    h1: "Marketing Agency for the Ottawa Valley",
    lead:
      "Kostaja works with service businesses across the Ottawa Valley: Ottawa, Arnprior, Renfrew, Pembroke, Petawawa, Carleton Place, Perth, and Almonte. Brand, website, and marketing systems built for operators whose reputation is bigger than their online presence.",
    sections: [
      {
        h: "What working in the Ottawa Valley actually looks like",
        body: [
          "The Ottawa Valley is a referral market. Most service businesses here have earned their reputations offline, in coffee shops and community boards and word of mouth. The gap is not the quality of the work. It is how that work shows up when someone finally Googles the business, opens the website on a phone, or reads the Google reviews before calling.",
          "Kostaja closes that gap. Same standard of work Kostaja does for Ottawa and Toronto operators, sized to Valley budgets and Valley expectations.",
        ],
      },
      {
        h: "Cities Kostaja works with across the Valley",
        rows: [
          { k: "Ottawa", v: "Full brand, website, and marketing systems for Ottawa service businesses and professional firms." },
          { k: "Arnprior", v: "Brand, website, and local visibility for Arnprior operators." },
          { k: "Renfrew", v: "Marketing and web work for Renfrew and Renfrew County service businesses." },
          { k: "Pembroke", v: "Brand and website work for Pembroke and Upper Ottawa Valley operators." },
          { k: "Petawawa", v: "Marketing for Petawawa and Garrison-adjacent service businesses." },
          { k: "Carleton Place", v: "Brand, website, and local search for Carleton Place operators." },
          { k: "Perth", v: "Marketing systems for Perth and Lanark County service businesses." },
          { k: "Almonte", v: "Brand and website work for Almonte and Mississippi Mills operators." },
        ],
      },
      {
        h: "How Kostaja works with Valley businesses",
        body: [
          "Remotely by default, with in-region visits arranged when the project benefits from it. Most engagements never require an in-person meeting, but Kostaja is close enough to be in the room when it matters. Written scopes, agreed pricing, plain-English deliverables, and studio leadership on the file from first call to launch.",
        ],
        links: [
          { label: "Ottawa page", to: "/ottawa-brand-marketing-agency" },
          { label: "Explore services", to: "/services" },
          { label: "Start a project", to: "/contact" },
        ],
      },
    ],
    faqs: [
      { q: "Is Kostaja based in the Ottawa Valley?", a: "Kostaja is a Canadian brand marketing agency serving Ottawa, the Ottawa Valley, and the rest of Canada. The studio works remotely by default, with in-region visits to Valley cities arranged when the project benefits from it." },
      { q: "How much do brand and website projects cost for a Valley business?", a: "Fixed-scope work for a Valley service business typically falls between one thousand and ten thousand Canadian dollars. Larger connected engagements are quoted after a short review. Every price is written down before work begins." },
      { q: "Do you help with Google Business Profile work in smaller Valley towns?", a: "Yes. Google Business Profile work is often the single highest-ROI first move for a small Valley operator. It is priced as a fixed-scope engagement and paired with a light ongoing rhythm the owner can maintain." },
    ],
  },

  "arnprior-marketing-agency": {
    key: "arnpriorMarketingAgency",
    slug: "arnprior-marketing-agency",
    eyebrow: "Local · Arnprior",
    h1: "Marketing Agency in Arnprior",
    lead:
      "Kostaja helps Arnprior service businesses look, read, and sell as capably as they actually work. Brand, website, Google Business Profile, and sales materials sized for Arnprior operators and their Ottawa Valley customers.",
    sections: citySection(
      "Arnprior",
      "Arnprior is a referral town with a growing commuter customer base to Ottawa. A finished brand and a working website close the gap between local reputation and how the business appears to new customers Googling from the highway or from home.",
      [
        { k: "Trades and home services", v: "Arnprior contractors, renovation firms, HVAC, and mechanical operators serving Arnprior and West Ottawa." },
        { k: "Health and wellness", v: "Clinics, dental, and physiotherapy practices serving Arnprior families." },
        { k: "Automotive and mechanical", v: "Independent shops, detailers, and service operators along the Highway 17 corridor." },
        { k: "Local retail and food", v: "Independent Arnprior operators along John Street and beyond." },
        { k: "Professional services", v: "Accountants, lawyers, and consultants working with Arnprior and West Ottawa clients." },
      ]
    ),
    faqs: cityCommonFaq("Arnprior"),
  },

  "renfrew-marketing-agency": {
    key: "renfrewMarketingAgency",
    slug: "renfrew-marketing-agency",
    eyebrow: "Local · Renfrew",
    h1: "Marketing Agency in Renfrew",
    lead:
      "Kostaja helps Renfrew service businesses close the gap between a strong local reputation and how the business appears online. Brand, website, and local search work for Renfrew and Renfrew County operators.",
    sections: citySection(
      "Renfrew",
      "Renfrew service businesses still earn most of their work through reputation and referral. The problem is that a referral checks the business online before calling. A finished Google profile, a working website, and a brand that reads professional make that online check go the right way.",
      [
        { k: "Trades and home services", v: "Renfrew contractors, mechanical, and home service operators serving Renfrew County." },
        { k: "Agriculture-adjacent services", v: "Service businesses that support Renfrew County's farming and rural economy." },
        { k: "Health and wellness", v: "Family clinics, dental, and wellness practices across Renfrew and area." },
        { k: "Automotive and mechanical", v: "Independent Renfrew shops and mechanical service operators." },
        { k: "Real estate and professional services", v: "Renfrew agents, accountants, and consultants serving Ottawa Valley clients." },
      ]
    ),
    faqs: cityCommonFaq("Renfrew"),
  },

  "pembroke-marketing-agency": {
    key: "pembrokeMarketingAgency",
    slug: "pembroke-marketing-agency",
    eyebrow: "Local · Pembroke",
    h1: "Marketing Agency in Pembroke",
    lead:
      "Kostaja helps Pembroke service businesses build a public presence that matches the work they already do. Brand, website, and local visibility for Pembroke and Upper Ottawa Valley operators.",
    sections: citySection(
      "Pembroke",
      "Pembroke service businesses often serve a wide catchment across the Upper Ottawa Valley. A brand that reads professional and a website that answers the real question mean a customer in Petawawa or Deep River can trust the business before making the drive.",
      [
        { k: "Trades and home services", v: "Pembroke contractors, renovation firms, and residential service operators." },
        { k: "Health and wellness", v: "Clinics, dental, and wellness practices across Pembroke and the Upper Valley." },
        { k: "Military and Garrison-adjacent services", v: "Businesses serving CFB Petawawa families and personnel across the region." },
        { k: "Retail and food", v: "Downtown Pembroke and Pembroke Street independent operators." },
        { k: "Professional services", v: "Accountants, lawyers, and consultants serving Upper Ottawa Valley clients." },
      ]
    ),
    faqs: cityCommonFaq("Pembroke"),
  },

  "petawawa-marketing-agency": {
    key: "petawawaMarketingAgency",
    slug: "petawawa-marketing-agency",
    eyebrow: "Local · Petawawa",
    h1: "Marketing Agency in Petawawa",
    lead:
      "Kostaja helps Petawawa service businesses look, read, and sell as capably as they actually work. Brand, website, and marketing systems built for Petawawa operators and the wider Garrison and Upper Valley community.",
    sections: citySection(
      "Petawawa",
      "Petawawa service businesses often work with a customer base that includes CFB Petawawa personnel, rotating military families, and long-term Upper Ottawa Valley residents. A brand and website that read as finished and welcoming from the first visit make the first contact easier in all three groups.",
      [
        { k: "Home services and trades", v: "Petawawa contractors, HVAC, and home service operators serving Garrison families and long-term residents." },
        { k: "Health and wellness", v: "Clinics, dental, physiotherapy, and wellness practices supporting Petawawa families." },
        { k: "Automotive and mechanical", v: "Independent Petawawa shops and mechanical service operators." },
        { k: "Retail and food", v: "Independent Petawawa restaurants, cafes, and retailers along Petawawa Boulevard and beyond." },
        { k: "Real estate", v: "Petawawa agents helping incoming Garrison families and outgoing rotations." },
      ]
    ),
    faqs: cityCommonFaq("Petawawa"),
  },

  "carleton-place-marketing-agency": {
    key: "carletonPlaceMarketingAgency",
    slug: "carleton-place-marketing-agency",
    eyebrow: "Local · Carleton Place",
    h1: "Marketing Agency in Carleton Place",
    lead:
      "Kostaja helps Carleton Place service businesses look as capable online as they are in person. Brand, website, and local visibility work for Carleton Place operators and their Ottawa-commuter customer base.",
    sections: citySection(
      "Carleton Place",
      "Carleton Place is a fast-growing town with a mix of long-standing local operators and new arrivals commuting to Ottawa. A brand that reads current and a website that respects the reader make an existing operation feel current and a new operation feel established.",
      [
        { k: "Trades and home services", v: "Carleton Place contractors, renovation firms, and residential service operators." },
        { k: "Health and wellness", v: "Clinics, dental, and wellness practices serving Carleton Place and Beckwith." },
        { k: "Real estate and mortgage", v: "Carleton Place agents and mortgage professionals working across Lanark and West Ottawa." },
        { k: "Retail and food", v: "Downtown Carleton Place independent restaurants, cafes, and retailers along Bridge Street." },
        { k: "Professional services", v: "Accountants, lawyers, and consultants serving Carleton Place and Ottawa-commuter clients." },
      ]
    ),
    faqs: cityCommonFaq("Carleton Place"),
  },

  "perth-marketing-agency": {
    key: "perthMarketingAgency",
    slug: "perth-marketing-agency",
    eyebrow: "Local · Perth",
    h1: "Marketing Agency in Perth",
    lead:
      "Kostaja helps Perth service businesses build a public presence that finally matches the character of the town and the quality of the work. Brand, website, and marketing systems for Perth and Lanark County operators.",
    sections: citySection(
      "Perth",
      "Perth is a heritage town where the storefront matters. A brand and website that read as considered and current can carry the same character offline as on. That combination turns first-time visitors into repeat customers and second-home owners into loyal locals.",
      [
        { k: "Retail and food", v: "Downtown Perth heritage retailers, restaurants, cafes, and food operators along Foster Street and Gore Street." },
        { k: "Health and wellness", v: "Clinics, dental, and wellness practices serving Perth and Lanark County families." },
        { k: "Trades and home services", v: "Perth contractors, renovation firms, and residential service operators." },
        { k: "Real estate", v: "Perth agents working with heritage buyers, second-home owners, and long-time locals." },
        { k: "Hospitality", v: "Perth inns, B&Bs, and event operators serving weekend and destination visitors." },
      ]
    ),
    faqs: cityCommonFaq("Perth"),
  },

  "almonte-marketing-agency": {
    key: "almonteMarketingAgency",
    slug: "almonte-marketing-agency",
    eyebrow: "Local · Almonte",
    h1: "Marketing Agency in Almonte",
    lead:
      "Kostaja helps Almonte and Mississippi Mills service businesses build a brand and public presence that reads as intentional as the town itself. Brand, website, and marketing work for Almonte operators and their local and commuter customers.",
    sections: citySection(
      "Almonte",
      "Almonte and Mississippi Mills operators work in a town with real design standards: historic Mill Street storefronts, a creative-sector reputation, and a visible tourist economy. A brand and website that read as considered and current support that context instead of fighting it.",
      [
        { k: "Retail and food", v: "Downtown Almonte independent retailers, restaurants, and cafes along Mill Street." },
        { k: "Trades and home services", v: "Almonte and Mississippi Mills contractors, renovation firms, and residential service operators." },
        { k: "Health and wellness", v: "Clinics, dental, and wellness practices serving Almonte and Ramsay Ward families." },
        { k: "Real estate", v: "Almonte agents working with heritage buyers, second-home owners, and Ottawa-commuter families." },
        { k: "Creators and makers", v: "Almonte makers, textile artists, and food producers building a real brand around a real practice." },
      ]
    ),
    faqs: cityCommonFaq("Almonte"),
  },

  // ---------------- INDUSTRY x SERVICE COMBO PAGES (8) ----------------
  "website-design-for-contractors-canada": {
    key: "websiteContractorsCanada",
    slug: "website-design-for-contractors-canada",
    eyebrow: "Website design · Contractors",
    h1: "Website Design for Contractors in Canada",
    lead:
      "Homeowners decide whether a contractor is worth calling before the site finishes loading. Kostaja builds contractor websites that answer the real questions, show the real work, and make booking an estimate feel safe.",
    sections: [
      {
        h: "Why most contractor websites lose leads",
        body: [
          "Most contractor websites read like a brochure. Big hero image, a paragraph about the company, a phone number, and a form. Homeowners in Canada are cautious. They want to see the actual work, understand the service area, know what a project like theirs typically involves, and hear from real customers before they call.",
          "A website that skips those steps sends the lead to whichever competitor answers them first.",
        ],
      },
      {
        h: "What a working contractor website does",
        rows: [
          { k: "Names the service area", v: "Cities and neighbourhoods served, without vague promises like 'GTA and beyond'." },
          { k: "Shows real projects", v: "Photos of actual jobs with plain-English descriptions of what was done and why." },
          { k: "Explains the estimate", v: "How estimates work, what they cost (if anything), and how long they take." },
          { k: "Answers the top objection", v: "Insurance, licensing, warranty, and timelines named on the page, not hidden in a PDF." },
          { k: "Ends every page on one CTA", v: "Book estimate, call now, or request a quote. One action per page." },
        ],
      },
      {
        h: "What Kostaja builds for contractors",
        body: [
          "Kostaja builds contractor websites as full systems: a homepage that respects the reader, a service page for each core trade, a service area page for each region, a project gallery that reads like a portfolio, a reviews page that shows real customer language, and a working estimate request flow.",
          "Every page ends on one clear next step. Everything is mobile-first because homeowners search from their phones while standing in the room that needs the work.",
        ],
      },
      {
        h: "Recommended pairing",
        body: [
          "A contractor website pairs best with a completed Google Business Profile, a review flow, and one round of print sales materials for estimate visits. All three can be built in the same engagement.",
        ],
        links: [
          { label: "Google Business Profile for home services", to: "/google-business-profile-for-home-services" },
          { label: "Explore services", to: "/services" },
          { label: "Home services industry", to: "/industries/home-services" },
        ],
      },
    ],
    faqs: [
      { q: "How long does a contractor website take to build?", a: "A tightly scoped contractor website usually takes four to eight weeks. A brand refresh plus website usually takes six to twelve weeks. Timelines depend on how quickly the contractor can supply photos, service areas, and project details." },
      { q: "Does Kostaja build in WordPress, Webflow, Wix, or Squarespace?", a: "Kostaja recommends the platform that fits the business, the budget, and the maintenance appetite. Most contractor sites are best served by a lightweight, fast, mobile-first build that the operator can maintain without a developer." },
      { q: "Do I need a service page for every service and every city?", a: "Not every combination, but every core service and every real service area should have its own page. That is how contractors get found by local buyers searching for the exact job they need." },
      { q: "Can Kostaja help with Google reviews too?", a: "Yes. Kostaja builds a review request flow that stays honest and steady after the site is live. Real customer language moves trust far more than a wall of five-star sentences." },
    ],
  },

  "website-design-for-law-firms-canada": {
    key: "websiteLawFirmsCanada",
    slug: "website-design-for-law-firms-canada",
    eyebrow: "Website design · Law firms",
    h1: "Website Design for Law Firms in Canada",
    lead:
      "Legal buyers scan for competence and calm. Kostaja builds law firm websites that read as serious counsel in the first ten seconds, and turn quiet inbound into booked consultations.",
    sections: [
      {
        h: "Why most law firm websites underperform",
        body: [
          "Most Canadian law firm websites over-invest in the firm's history and under-invest in the buyer's actual question. Prospective clients arrive with a legal problem, not a curiosity about the firm's 1987 founding. If the site does not answer the problem in plain English, the buyer leaves.",
        ],
      },
      {
        h: "What a working law firm website does",
        rows: [
          { k: "Practice-area pages", v: "One dedicated page per service (family, real estate, immigration, corporate, litigation) that names the outcome, the process, and the audience." },
          { k: "Consultation clarity", v: "How long consultations are, whether they are free, and how to book one, on every page." },
          { k: "Lawyer bios that read senior", v: "Short, calm bios that lead with the buyer's problem, not the lawyer's credentials list." },
          { k: "Honest positioning", v: "No guaranteed outcomes, no theatrical language. Law society compliance built into the copy." },
          { k: "Fast mobile experience", v: "Most legal search happens on phones in stressful moments. Speed and clarity are non-negotiable." },
        ],
      },
      {
        h: "What Kostaja builds for law firms",
        body: [
          "Kostaja builds law firm websites as calm, structured systems. A homepage that respects the reader. A practice-area page for every real service. Lawyer bios that read as senior counsel. A resource section that supports authority without giving free legal advice. A working consultation booking flow.",
          "Copy is compliance-aware and reviewed for Ontario, BC, Alberta, and other provincial law society guidelines where applicable.",
        ],
      },
      {
        h: "Recommended pairing",
        body: [
          "A law firm website pairs best with a completed Google Business Profile, review flow, and pitch-ready sales materials for referral partners.",
        ],
        links: [
          { label: "Professional services industry", to: "/industries/professional-services" },
          { label: "Legal services industry", to: "/industries/legal-services" },
          { label: "Explore services", to: "/services" },
        ],
      },
    ],
    faqs: [
      { q: "Is Kostaja compliant with Canadian law society advertising guidelines?", a: "Kostaja writes law firm copy with provincial law society guidelines in mind and reviews language for common compliance risks (guaranteed outcomes, comparative claims, testimonial rules). Final compliance review remains the responsibility of the firm's principal." },
      { q: "How long does a law firm website take to build?", a: "A tightly scoped law firm website usually takes six to ten weeks. A brand refresh plus website usually takes eight to fourteen weeks." },
      { q: "Do you build bilingual websites for Quebec and Ontario firms?", a: "Yes. Bilingual builds are handled through vetted French copy partners and reviewed for Quebec-specific compliance requirements." },
      { q: "Can Kostaja help with intake automation for a law firm?", a: "Yes. Kostaja integrates working intake forms and can layer an AI receptionist for after-hours inquiry handling and follow-up." },
    ],
  },

  "website-design-for-restaurants-canada": {
    key: "websiteRestaurantsCanada",
    slug: "website-design-for-restaurants-canada",
    eyebrow: "Website design · Restaurants",
    h1: "Website Design for Restaurants in Canada",
    lead:
      "Restaurant guests decide with their eyes in about six seconds. Kostaja builds Canadian restaurant websites that make the menu easy to read, the reservation easy to book, and the room easy to remember.",
    sections: [
      {
        h: "Why most restaurant websites hurt bookings",
        body: [
          "Most restaurant websites bury the menu, hide the address, and route reservations through a slow modal. Guests are searching on their phone, often on the sidewalk deciding where to eat next. If the menu, hours, and reservation link are not visible immediately, the booking goes to whoever loads faster.",
        ],
      },
      {
        h: "What a working restaurant website does",
        rows: [
          { k: "Menu-first design", v: "The menu is above the fold on mobile. Not linked as a PDF. Readable, searchable, and up to date." },
          { k: "One-tap reservation", v: "OpenTable, Resy, or direct booking integrated so the guest never leaves the page." },
          { k: "Real photos", v: "Honest photos of the food, the room, and the team. Stock imagery kills trust." },
          { k: "Hours and address up front", v: "Hours, address, phone, and directions link on every page." },
          { k: "Private events and catering, if applicable", v: "Dedicated pages for events, catering, and buyouts. Not buried in a contact form." },
        ],
      },
      {
        h: "What Kostaja builds for restaurants",
        body: [
          "Kostaja builds restaurant websites as guest-first systems. A menu that stays current without needing a developer. A reservation flow that works on the first tap. A hours-and-address block that appears on every page. Real photos of the food and the room. A private events and catering path for higher-margin bookings.",
          "Every page is mobile-first because every restaurant search happens on a phone.",
        ],
      },
      {
        h: "Recommended pairing",
        body: [
          "A restaurant website pairs best with a completed Google Business Profile, a review flow, and a menu design refresh for print and PDF.",
        ],
        links: [
          { label: "Google Business Profile for restaurants", to: "/google-business-profile-for-restaurants" },
          { label: "Restaurants and bars industry", to: "/industries/restaurants-and-bars" },
          { label: "Explore services", to: "/services" },
        ],
      },
    ],
    faqs: [
      { q: "Can we update the menu ourselves without a developer?", a: "Yes. Kostaja builds restaurant websites so the operator can update the menu, hours, and specials without touching code." },
      { q: "Do you integrate OpenTable, Resy, or Tock?", a: "Yes. Kostaja integrates the reservation platform the restaurant already uses, or recommends one if the restaurant is choosing." },
      { q: "How long does a restaurant website take to build?", a: "A tightly scoped restaurant website usually takes four to six weeks. Menu design and photography can add two to four weeks depending on scope." },
      { q: "Do you help with private events and catering pages?", a: "Yes. Kostaja builds dedicated pages for events, catering, and buyouts, which is where most restaurants leave real revenue on the table." },
    ],
  },

  "branding-for-real-estate-agents-canada": {
    key: "brandingRealEstateAgentsCanada",
    slug: "branding-for-real-estate-agents-canada",
    eyebrow: "Branding · Real estate",
    h1: "Branding for Real Estate Agents in Canada",
    lead:
      "Real estate buyers and sellers pick an agent in seconds, mostly on trust signals. Kostaja builds personal brands for Canadian agents that read senior, feel current, and earn the second conversation.",
    sections: [
      {
        h: "Why real estate personal brands feel interchangeable",
        body: [
          "Most Canadian real estate agents use brokerage-provided templates for cards, signs, and social. Those templates make every agent look like every other agent. In a market where the client's biggest transaction is on the table, looking the same as everyone else is a losing position.",
        ],
      },
      {
        h: "What a working real estate personal brand does",
        rows: [
          { k: "Ownable identity", v: "A logo, colour system, and typography that stay recognizable across listings, social, print, and signage." },
          { k: "Positioning that reads senior", v: "A short line about who the agent works with best and what they are known for, in plain English." },
          { k: "Consistent social presence", v: "Instagram, Facebook, and LinkedIn that all look and read like the same agent, not three." },
          { k: "Sales materials that earn the listing", v: "Listing presentation, buyer package, and open house materials that feel more considered than the brokerage default." },
        ],
      },
      {
        h: "What Kostaja builds for real estate agents",
        body: [
          "Kostaja builds a personal real estate brand as a working system: name, mark, colour, typography, voice, and the rules for using them across every touchpoint. It ships with listing presentation templates, buyer packages, social profile assets, and a personal landing page that lives on your domain, not just on the brokerage's.",
        ],
      },
      {
        h: "Recommended pairing",
        body: [
          "A real estate personal brand pairs best with a working personal website, a Google Business Profile as a professional, and a small print kit for listings and open houses.",
        ],
        links: [
          { label: "Real estate industry", to: "/industries/real-estate" },
          { label: "Explore services", to: "/services" },
          { label: "See selected work", to: "/portfolio" },
        ],
      },
    ],
    faqs: [
      { q: "Is a personal brand allowed under my brokerage's rules?", a: "Most Canadian brokerages (RE/MAX, Royal LePage, Century 21, Sotheby's, EXP, Chestnut Park, and boutique brokerages) allow personal branding within specific rules on logo placement, disclaimers, and disclosure of brokerage affiliation. Kostaja builds within those rules and confirms compliance with your broker before launch." },
      { q: "How long does a real estate brand build take?", a: "A tightly scoped personal brand system usually takes four to eight weeks. A brand plus personal website usually takes six to twelve weeks." },
      { q: "Will my brand still work if I switch brokerages?", a: "Yes. Kostaja builds a personal brand that belongs to you, not to your brokerage. Brokerage-specific elements (logos, disclaimers) are separated so switching brokerages is a routine update, not a rebuild." },
      { q: "Do you help with listing photography and video?", a: "Kostaja does not shoot listings directly. Kostaja can recommend photographers and videographers in your market who fit the brand." },
    ],
  },

  "branding-for-dentists-canada": {
    key: "brandingDentistsCanada",
    slug: "branding-for-dentists-canada",
    eyebrow: "Branding · Dentists",
    h1: "Branding for Dental Practices in Canada",
    lead:
      "Dental patients pick a practice on how safe and current it feels. Kostaja builds dental practice brands that read calm, current, and worth the drive, so new patients pick up the phone and existing ones stay.",
    sections: [
      {
        h: "Why most dental practice brands feel dated",
        body: [
          "Most Canadian dental practice brands were designed a decade ago, with clinical blue palettes, generic tooth logos, and stock imagery that says nothing about the practice. Patients read that as tired, not established. In a market where families choose a dentist and stay for years, first impressions decide the pipeline.",
        ],
      },
      {
        h: "What a working dental practice brand does",
        rows: [
          { k: "Reads current without reading trendy", v: "Colour, typography, and layout that will still feel modern in five years." },
          { k: "Signals calm and safety", v: "Visual and copy choices that reduce anxiety for new patients." },
          { k: "Works across every surface", v: "From signage and business cards to Instagram and the front-desk monitor." },
          { k: "Supports treatment pages", v: "A design system that lets each treatment page read as its own service, not a template." },
        ],
      },
      {
        h: "What Kostaja builds for dental practices",
        body: [
          "Kostaja builds dental brands as full systems: name, mark, colour, typography, voice, patient-facing print, and rules for social and treatment page design. It is designed to support both general and specialty practices (family, cosmetic, orthodontics, periodontics, oral surgery), and to hold up as the practice adds locations or providers.",
        ],
      },
      {
        h: "Recommended pairing",
        body: [
          "A dental practice brand pairs best with a modern website, a Google Business Profile with treatment services listed, and a review flow that respects patient privacy.",
        ],
        links: [
          { label: "Health and wellness industry", to: "/industries/health-and-wellness" },
          { label: "Explore services", to: "/services" },
          { label: "Apply to work with Kostaja", to: "/waitlist" },
        ],
      },
    ],
    faqs: [
      { q: "How long does a dental practice brand build take?", a: "A tightly scoped brand foundation for a dental practice usually takes four to eight weeks. A brand plus website usually takes eight to fourteen weeks." },
      { q: "Do you help multi-location or dental group brands?", a: "Yes. Kostaja builds parent brand systems that support multi-location practices, with rules for how each location expresses the shared identity." },
      { q: "Can Kostaja help with treatment page copy?", a: "Yes. Treatment page copy is included in a full website engagement, written in plain English and reviewed for regulatory and CDSPI-adjacent compliance." },
      { q: "What about patient reviews and privacy?", a: "Kostaja builds a review flow that keeps requests within Canadian privacy norms and does not use patient photos or names without written consent." },
    ],
  },

  "branding-for-financial-advisors-canada": {
    key: "brandingFinancialAdvisorsCanada",
    slug: "branding-for-financial-advisors-canada",
    eyebrow: "Branding · Financial advisors",
    h1: "Branding for Financial Advisors in Canada",
    lead:
      "Financial buyers need to feel respected before they feel sold to. Kostaja builds Canadian financial advisor brands that read calm, senior, and clear, so the discovery call happens and the second meeting is easy.",
    sections: [
      {
        h: "Why most financial advisor brands feel generic",
        body: [
          "Most Canadian financial advisors use their firm's stock templates for cards, decks, and social. The templates are compliant, but they are also interchangeable. Prospective clients cannot distinguish one advisor from another, and they decide on personal referral alone, which caps growth.",
        ],
      },
      {
        h: "What a working financial advisor brand does",
        rows: [
          { k: "Reads senior and calm", v: "Colour, typography, and voice that signal considered, not aggressive." },
          { k: "Names the ideal client", v: "Copy that quietly makes the right prospect say 'this person understands me' and the wrong prospect self-filter." },
          { k: "Explains the work in plain English", v: "How the advisor works, what a first meeting looks like, and what happens after signing on." },
          { k: "Holds up in a compliance review", v: "Copy and visuals built with IIROC, MFDA (now CIRO), and provincial insurance-council rules in mind." },
        ],
      },
      {
        h: "What Kostaja builds for financial advisors",
        body: [
          "Kostaja builds advisor brands as full personal or team systems: name treatment, mark, colour, typography, voice, and rules for social, print, and web. It ships with meeting materials, a discovery-call one-pager, and a landing page that lives on the advisor's domain.",
          "All copy is reviewed for common Canadian compliance risks: guaranteed returns, comparative claims, testimonial rules, and disclosure requirements.",
        ],
      },
      {
        h: "Recommended pairing",
        body: [
          "A financial advisor brand pairs best with a working personal website, a Google Business Profile as a professional, and pitch-ready print materials for referral partners.",
        ],
        links: [
          { label: "Financial services industry", to: "/industries/financial-services" },
          { label: "Explore services", to: "/services" },
          { label: "Apply to work with Kostaja", to: "/waitlist" },
        ],
      },
    ],
    faqs: [
      { q: "Is Kostaja compliant with CIRO, provincial insurance councils, and dealer requirements?", a: "Kostaja writes advisor copy with CIRO, provincial insurance council, and dealer marketing guidelines in mind and flags common compliance risks. Final compliance review remains the responsibility of the advisor's compliance officer or dealer." },
      { q: "Can I use this brand if I switch dealers?", a: "Yes. Kostaja separates dealer-specific requirements (disclaimers, logos) from the advisor's personal brand so that switching dealers is a routine update, not a rebuild." },
      { q: "How long does an advisor brand build take?", a: "A tightly scoped personal brand for an advisor usually takes four to eight weeks. A brand plus website usually takes six to twelve weeks." },
      { q: "Do you help with pitch decks and client meeting materials?", a: "Yes. Kostaja builds pitch-ready decks, discovery-call one-pagers, and meeting materials on the same brand system." },
    ],
  },

  "google-business-profile-for-home-services": {
    key: "gbpHomeServices",
    slug: "google-business-profile-for-home-services",
    eyebrow: "Google Business Profile · Home services",
    h1: "Google Business Profile for Home Services in Canada",
    lead:
      "Homeowners search on their phone from the room that needs the work. Kostaja completes and optimizes Google Business Profiles for Canadian home service operators, so the phone rings while the competitor is still on hold.",
    sections: [
      {
        h: "Why home services live or die on Google Business Profile",
        body: [
          "Home service buyers do not scroll to page two. They tap the top few results in the Google map pack, look at reviews and photos, and dial the first business that looks legitimate and available. A finished, current Google Business Profile is the single largest source of new work for most Canadian home service operators.",
        ],
      },
      {
        h: "What a working home services GBP includes",
        rows: [
          { k: "Complete categories and services", v: "Primary category set correctly, every real service listed, no dead ends." },
          { k: "Real photos", v: "Photos of actual jobs, not stock imagery. Google favours real, dated, geo-tagged photos." },
          { k: "Accurate service area", v: "Cities and neighbourhoods served, without vague overpromises." },
          { k: "Answered reviews", v: "Every review, positive or negative, answered calmly and quickly." },
          { k: "Weekly posts and offers", v: "A steady, low-effort posting rhythm that signals an active, real business." },
          { k: "Q&A management", v: "Common questions answered on the profile itself, before they hit the phone." },
        ],
      },
      {
        h: "What Kostaja does",
        body: [
          "Kostaja audits, completes, and optimizes home services Google Business Profiles as a fixed-scope engagement. Every service, category, area, photo, and hour is set correctly. A review flow is built. A posting cadence is set. The owner receives a short maintenance playbook that keeps the profile alive without daily attention.",
        ],
      },
      {
        h: "Recommended pairing",
        body: [
          "A GBP engagement pairs best with a working contractor website and a review flow that keeps the profile healthy over time.",
        ],
        links: [
          { label: "Website design for contractors", to: "/website-design-for-contractors-canada" },
          { label: "Home services industry", to: "/industries/home-services" },
          { label: "Explore services", to: "/services" },
        ],
      },
    ],
    faqs: [
      { q: "How long until a Google Business Profile starts producing calls?", a: "A completed and optimized profile usually starts producing measurable call and direction volume within four to eight weeks. Established profiles that get finished properly often see a lift within two weeks." },
      { q: "Does Kostaja guarantee first-place ranking on Google Maps?", a: "No. Nobody credible does. Kostaja builds the profile foundation that gives a home services business a real shot at ranking, and typically improves visibility meaningfully within the first quarter." },
      { q: "What if my profile was suspended or my competitors filed false reports?", a: "Kostaja can help with reinstatement documentation and Google Business Profile support escalation. This is a scoped project, not a guarantee, but most legitimate businesses recover with the right paperwork." },
      { q: "Do you help with reviews too?", a: "Yes. A GBP engagement includes building a review request flow so the profile stays healthy after the initial optimization." },
    ],
  },

  "google-business-profile-for-restaurants": {
    key: "gbpRestaurants",
    slug: "google-business-profile-for-restaurants",
    eyebrow: "Google Business Profile · Restaurants",
    h1: "Google Business Profile for Restaurants in Canada",
    lead:
      "Diners search 'restaurants near me' on the sidewalk and decide in seconds. Kostaja completes and optimizes Google Business Profiles for Canadian restaurants, so the walk-in comes in instead of walking past.",
    sections: [
      {
        h: "Why restaurants live or die on Google Business Profile",
        body: [
          "For most Canadian restaurants, Google Business Profile is the single largest driver of new customers. Diners search 'restaurants near me' or the neighbourhood name, look at photos and reviews, and pick a place in about ten seconds. An incomplete or out-of-date profile costs walk-ins every night.",
        ],
      },
      {
        h: "What a working restaurant GBP includes",
        rows: [
          { k: "Correct primary category", v: "Not just 'Restaurant'. The specific cuisine or format that matches what you actually serve." },
          { k: "Menu link and menu photos", v: "Direct link to the menu, plus real photos of actual dishes." },
          { k: "Interior and exterior photos", v: "Real photos of the room, the patio, the bar, and the storefront. Diners want to see what they are walking into." },
          { k: "Reservation link", v: "OpenTable, Resy, Tock, or direct booking link connected to the profile." },
          { k: "Accurate hours, including holidays", v: "Holiday hours updated in advance. Nothing kills trust like a locked door at posted-open hours." },
          { k: "Answered reviews", v: "Every review, positive or critical, answered warmly and briefly." },
        ],
      },
      {
        h: "What Kostaja does",
        body: [
          "Kostaja audits, completes, and optimizes restaurant Google Business Profiles as a fixed-scope engagement. Category, cuisine, menu link, reservation link, photos, hours, and holiday hours are all set correctly. A review response template and a light posting rhythm are set up. The operator receives a short maintenance playbook.",
        ],
      },
      {
        h: "Recommended pairing",
        body: [
          "A restaurant GBP engagement pairs best with a working restaurant website, a menu design refresh, and a review flow that keeps the profile healthy through the year.",
        ],
        links: [
          { label: "Website design for restaurants", to: "/website-design-for-restaurants-canada" },
          { label: "Restaurants and bars industry", to: "/industries/restaurants-and-bars" },
          { label: "Explore services", to: "/services" },
        ],
      },
    ],
    faqs: [
      { q: "How quickly can a restaurant profile improve walk-in traffic?", a: "Restaurants that go from incomplete to fully optimized usually see measurable lift in profile views, direction requests, and phone calls within two to six weeks." },
      { q: "Do you handle food photography?", a: "Kostaja does not shoot food directly, but can recommend local food photographers who fit the brand and price range. Photos are the highest-ROI upgrade for most restaurant profiles." },
      { q: "Can Kostaja help with responding to reviews on an ongoing basis?", a: "Yes, as part of a light ongoing engagement. Most restaurants can also self-manage using the response templates and playbook Kostaja provides." },
      { q: "Does this help with third-party listing sites like Yelp, OpenTable profiles, and TripAdvisor?", a: "Kostaja focuses on Google Business Profile as the primary lever because it produces the largest measurable lift. Consistency across Yelp, OpenTable, and TripAdvisor is included in scoping if relevant." },
    ],
  },
};

export const GROWTH_PAGE_SLUGS = Object.keys(GROWTH_PAGES);
